import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calculator, Car, Wallet, AlertCircle } from 'lucide-react';
import { fadeInUp } from '../../lib/animations';
import DecimalInput from '../ui/DecimalInput';


const CalculadoraCustoKm: React.FC = () => {
  const [valorCarro, setValorCarro] = useState(50000);
  const [kmMensal, setKmMensal] = useState(1000);
  const [consumo, setConsumo] = useState(10);
  const [precoCombustivel, setPrecoCombustivel] = useState(5.50);
  const [ipvaAnual, setIpvaAnual] = useState(2000);
  const [seguroAnual, setSeguroAnual] = useState(2500);
  const [manutencaoAnual, setManutencaoAnual] = useState(1500);

  // Calcula depreciação média (10% ao ano)
  const depreciacaoAnual = valorCarro * 0.10;

  // Consumos mensais
  const depreMensal = depreciacaoAnual / 12;
  const ipvaMensal = ipvaAnual / 12;
  const seguroMensal = seguroAnual / 12;
  const manutencaoMensal = manutencaoAnual / 12;

  const litrosMensal = kmMensal && consumo ? kmMensal / consumo : 0;
  const combustivelMensal = litrosMensal * precoCombustivel;

  const custoFixoMensal = ipvaMensal + seguroMensal + depreciacaoAnual / 12;
  const custoVariavelMensal = manutencaoMensal + combustivelMensal;
  const custoTotalMensal = custoFixoMensal + custoVariavelMensal;

  const custoPorKm = kmMensal > 0 ? custoTotalMensal / kmMensal : 0;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <motion.div variants={fadeInUp} className="overflow-hidden rounded-2xl bg-slate-900 shadow-xl shadow-slate-900/40 border border-slate-800">
        <div className="bg-amber-400 p-6 sm:p-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-950/30">
            <Calculator className="h-7 w-7 text-white" strokeWidth={1.8} />
          </div>
          <h2 className="font-display text-2xl font-bold text-white mb-2">Calculadora de Custo por KM</h2>
          <p className="text-slate-300 text-sm max-w-lg mx-auto">
            Descubra o TCO (Custo Total de Propriedade). Manter um carro vai muito além da parcela e da gasolina!
          </p>
        </div>

        <div className="p-6 sm:p-8 grid gap-8 md:grid-cols-2">
          {/* Dados do Veículo */}
          <div className="space-y-4">
            <h3 className="font-semibold text-white flex items-center gap-2 border-b border-slate-800 pb-2">
              <Car className="h-5 w-5 text-amber-500" />
              Uso do Veículo
            </h3>

            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">Valor do Veículo (R$)</label>
              <DecimalInput value={valorCarro} onChange={setValorCarro} className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white focus:border-amber-400 focus:outline-none" />
              <p className="text-[11px] text-slate-400 mt-1">Usado para calcular a depreciação (10% a.a)</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">KM / Mês</label>
                <DecimalInput value={kmMensal} onChange={setKmMensal} className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white focus:border-amber-400 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Consumo (km/l)</label>
                <DecimalInput value={consumo} onChange={setConsumo} className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white focus:border-amber-400 focus:outline-none" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">Preço Combustível (R$)</label>
              <DecimalInput value={precoCombustivel} onChange={setPrecoCombustivel} className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white focus:border-amber-400 focus:outline-none" />
            </div>
          </div>

          {/* Despesas Anuais */}
          <div className="space-y-4">
            <h3 className="font-semibold text-white flex items-center gap-2 border-b border-slate-800 pb-2">
              <Wallet className="h-5 w-5 text-amber-500" />
              Despesas Anuais
            </h3>

            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">IPVA + Taxas Anual (R$)</label>
              <DecimalInput value={ipvaAnual} onChange={setIpvaAnual} className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white focus:border-amber-400 focus:outline-none" />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">Seguro Anual (R$)</label>
              <DecimalInput value={seguroAnual} onChange={setSeguroAnual} className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white focus:border-amber-400 focus:outline-none" />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">Estimativa Manutenção/Pneus Anual (R$)</label>
              <DecimalInput value={manutencaoAnual} onChange={setManutencaoAnual} className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white focus:border-amber-400 focus:outline-none" />
            </div>
          </div>
        </div>

        {/* Resultado Final */}
        <div className="border-t border-slate-800 bg-slate-800 flex flex-col p-6 sm:p-8">
          <h3 className="font-display text-xl font-bold text-white text-center mb-6">
            O Custo Real do seu Veículo
          </h3>

          <div className="grid sm:grid-cols-2 gap-6 mb-6">
            <div className="bg-slate-900 rounded-xl p-5 border border-slate-700 text-center shadow-sm">
              <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1">Custo Total Mensal</p>
              <div className="font-display text-2xl font-bold text-slate-300">
                R$ {custoTotalMensal.toFixed(2)}
              </div>
              <p className="text-xs text-slate-400 mt-2">Fixo + Variável + Depreciação</p>
            </div>

            <div className="bg-amber-400/10 rounded-xl p-5 border border-amber-400/30 text-center shadow-sm shadow-amber-900/20">
              <p className="text-xs text-amber-400 font-bold uppercase tracking-wider mb-1">
                Custo Real por KM
              </p>
              <div className="font-display text-3xl font-black text-amber-400">
                R$ {custoPorKm.toFixed(2)}
              </div>
            </div>
          </div>

          <div className="bg-blue-400/10 text-blue-200 rounded-lg p-4 text-sm flex gap-3 text-left">
            <AlertCircle className="h-5 w-5 shrink-0 text-blue-400" />
            <p>
              Nessa simulação, <strong>R$ {combustivelMensal.toFixed(2)}</strong> representam o combustível. O restante (<strong>R$ {(custoTotalMensal - combustivelMensal).toFixed(2)}</strong>) são despesas invisíveis e desvalorização.
            </p>
          </div>
        </div>
      </motion.div>

    </div>
  );
};

export default CalculadoraCustoKm;




